// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React from 'react';
import { ANOVATable } from '@/components/results/SharedTables';

export function CorrelationView({ data }) {
  if (!data) return null;
  const corr = data.pearson_correlation || {};
  const sigMatrix = data.significance_matrix || {};
  const pvals = data.p_values || {};
  const vars = Object.keys(corr);
  const desc = data.descriptive_stats || {};
  const partials = data.partial_correlations || [];

  const getCellColor = (r, isDiag) => {
    if (isDiag) return 'bg-gray-200';
    const abs_r = Math.abs(r);
    if (isNaN(r)) return '';
    if (r > 0) {
      if (abs_r > 0.8) return 'bg-emerald-600 text-white';
      if (abs_r > 0.6) return 'bg-emerald-400 text-white';
      if (abs_r > 0.4) return 'bg-emerald-200';
      if (abs_r > 0.2) return 'bg-emerald-100';
      return 'bg-emerald-50';
    } else {
      if (abs_r > 0.8) return 'bg-red-600 text-white';
      if (abs_r > 0.6) return 'bg-red-400 text-white';
      if (abs_r > 0.4) return 'bg-red-200';
      if (abs_r > 0.2) return 'bg-red-100';
      return 'bg-red-50';
    }
  };

  const fmtP = (p) => {
    if (p === null || p === undefined) return '-';
    if (p < 0.001) return '<0.001';
    return typeof p === 'number' ? p.toFixed(4) : String(p);
  };

  return (
    <div className="space-y-6" data-testid="correlation-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5 border border-[#2ECC71]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Descriptive Statistics */}
      {Object.keys(desc).length > 0 && (
        <div data-testid="corr-descriptive-stats">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Descriptive Statistics</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#2ECC71] text-white">
              {['Variable', 'n', 'Mean', 'SD', 'Min', 'Max'].map(h => (
                <th key={h} className="px-3 py-2 text-center font-semibold border border-[#27AE60]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(desc).map(([v, s], i) => (
                <tr key={v} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                  <td className="px-3 py-1.5 font-semibold border border-[#E8E6E1]">{v}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.n}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Mean?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.SD?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Min?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Max?.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pearson Correlation Matrix with color-coded cells */}
      {vars.length > 0 && (
        <div data-testid="corr-pearson-matrix">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Pearson Correlation Matrix</h4>
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead><tr className="bg-[#2ECC71] text-white">
                <th className="px-3 py-2 text-left font-semibold border border-[#27AE60] sticky left-0 bg-[#2ECC71] z-10 min-w-[100px]">Variable</th>
                {vars.map(c => <th key={c} className="px-3 py-2 text-center font-semibold border border-[#27AE60] min-w-[80px]">{c}</th>)}
              </tr></thead>
              <tbody>
                {vars.map(row => (
                  <tr key={row}>
                    <td className="px-3 py-1.5 font-semibold text-[#2C2A28] border border-[#E8E6E1] bg-[#F5F4F2] sticky left-0 z-10">{row}</td>
                    {vars.map(col => {
                      const val = corr[row]?.[col];
                      const num = typeof val === 'number' ? val : parseFloat(val);
                      const isDiag = row === col;
                      const sig = sigMatrix[row]?.[col] || '';
                      const stars = (sig === '**' || sig === '*') ? sig : '';
                      return (
                        <td key={col} className={`px-3 py-1.5 text-center font-mono border border-[#E8E6E1] ${getCellColor(num, isDiag)}`}>
                          {isDiag ? <span className="font-bold">1.000</span> : (
                            <span className="font-semibold">
                              {!isNaN(num) ? num.toFixed(3) : '-'}
                              {stars && <sup className="ml-0.5 font-bold">{stars}</sup>}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[#8A8580] mt-2">** p&lt;0.001 (Highly Significant) &nbsp;|&nbsp; * p&lt;0.05 (Significant)</p>
        </div>
      )}

      {/* P-Values Matrix */}
      {Object.keys(pvals).length > 0 && typeof pvals === 'object' && !Array.isArray(pvals) && (
        <div data-testid="corr-pvalues-matrix">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">P-Values Matrix</h4>
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead><tr className="bg-[#34495E] text-white">
                <th className="px-3 py-2 text-left font-semibold border border-[#2C3E50] sticky left-0 bg-[#34495E] z-10 min-w-[100px]">Variable</th>
                {vars.map(c => <th key={c} className="px-3 py-2 text-center font-semibold border border-[#2C3E50] min-w-[80px]">{c}</th>)}
              </tr></thead>
              <tbody>
                {vars.map((row, ri) => (
                  <tr key={row} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1.5 font-semibold border border-[#E8E6E1] bg-[#F5F4F2] sticky left-0 z-10">{row}</td>
                    {vars.map(col => {
                      const p = pvals[row]?.[col];
                      return (
                        <td key={col} className={`px-3 py-1.5 text-center font-mono border border-[#E8E6E1] ${row === col ? 'bg-gray-200' : p !== null && p < 0.05 ? 'bg-amber-50' : ''}`}>
                          {row === col ? '-' : fmtP(p)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Partial Correlations Table */}
      {partials.length > 0 && (
        <div data-testid="corr-partial-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Partial Correlations</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#8E44AD] text-white">
              {['Variable 1', 'Variable 2', 'Controlling For', 'Partial r', 'p-value', 'Sig'].map(h => (
                <th key={h} className="px-3 py-2 text-center font-semibold border border-[#7D3C98]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {partials.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F0FF]'}>
                  <td className="px-3 py-1.5 font-medium border border-[#E8E6E1]">{row.var1}</td>
                  <td className="px-3 py-1.5 font-medium border border-[#E8E6E1]">{row.var2}</td>
                  <td className="px-3 py-1.5 text-[#8A8580] border border-[#E8E6E1]">{row.controlling}</td>
                  <td className={`px-3 py-1.5 text-center font-mono font-semibold border border-[#E8E6E1] ${getCellColor(row.partial_r, false)}`}>{row.partial_r?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{fmtP(row.p_value)}</td>
                  <td className="px-3 py-1.5 text-center border border-[#E8E6E1]">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${row.significance === '**' ? 'bg-emerald-500 text-white' : row.significance === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{row.significance}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Heatmap */}
      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="correlation-heatmap">
          <div className="bg-[#2ECC71]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#27AE60] uppercase tracking-wider">Correlation Heatmap</h4>
          </div>
          <img src={`data:image/png;base64,${data.chart}`} alt="Correlation Heatmap" className="w-full" />
        </div>
      )}

      {/* Scatter Plot Matrix (≤ 6 traits) or Top-K Pair Scatters (> 6 traits) */}
      {data.scatter_matrix_chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="scatter-matrix">
          <div className="bg-[#3498DB]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#2980B9] uppercase tracking-wider">
              {data.scatter_top_k_pairs && data.scatter_top_k_pairs.length > 0
                ? `Top ${data.scatter_top_k_pairs.length} Most-Correlated Trait Pairs`
                : 'Scatter Plot Matrix with Regression Lines'}
            </h4>
          </div>
          <img src={`data:image/png;base64,${data.scatter_matrix_chart}`} alt="Scatter Matrix" className="w-full" />
          {data.scatter_matrix_skipped_reason && (
            <div className="px-4 py-2.5 text-[12px] leading-relaxed bg-[#F8FAFC] border-t border-[#E8E6E1] text-[#475569] italic" data-testid="scatter-matrix-note">
              {data.scatter_matrix_skipped_reason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   REGRESSION VIEW
============================================================ */


export function RegressionView({ data }) {
  if (!data) return null;
  const ms = data.model_summary || {};
  const coefTable = data.coefficients_table || {};
  const anova = data.anova_table || {};
  const desc = data.descriptive_stats || {};

  const fmtP = (p) => {
    if (p === null || p === undefined) return '-';
    if (typeof p === 'number' && p < 0.001) return '<0.001';
    return typeof p === 'number' ? p.toFixed(4) : String(p);
  };

  return (
    <div className="space-y-6" data-testid="regression-view">
      {data.interpretation && (
        <div className="bg-[#EBF5FB] rounded-2xl p-5 border border-[#2980B9]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Model Equation */}
      {data.equation && (
        <div className="bg-white rounded-xl p-4 border-2 border-[#3498DB] text-center">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Regression Equation</p>
          <p className="font-mono text-lg font-bold text-[#2C3E50]">{data.equation}</p>
        </div>
      )}

      {/* Model Summary Cards */}
      {Object.keys(ms).length > 0 && (
        <div data-testid="regression-model-summary">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Model Summary</h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(ms).map(([k, v]) => (
              <div key={k} className="bg-[#3498DB]/5 rounded-xl px-3 py-3 text-center border border-[#3498DB]/15">
                <div className="text-xl font-bold text-[#2980B9]">{typeof v === 'number' ? v.toFixed(4) : v}</div>
                <div className="text-[10px] text-[#8A8580] uppercase mt-1">{k.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ANOVA Table */}
      <ANOVATable anovaTable={anova} title="ANOVA Table" color="#2980B9" />

      {/* Coefficients Table */}
      {Object.keys(coefTable).length > 0 && (
        <div data-testid="regression-coefficients">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Coefficients Table</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#2C3E50] text-white">
                {['Predictor', 'B', 'Std. Error', 't', 'p-value', '95% CI Lower', '95% CI Upper'].map(h => (
                  <th key={h} className="px-3 py-2 text-center font-semibold border border-[#34495E]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {Object.entries(coefTable).map(([name, c], i) => (
                  <tr key={name} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1.5 font-semibold border border-[#E8E6E1]">{name === 'Intercept' ? 'Intercept (B0)' : `${name} (B1)`}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{c.B?.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{c.SE?.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{c.t?.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{fmtP(c.P)}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{c.CI_lower?.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{c.CI_upper?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Descriptive Statistics */}
      {Object.keys(desc).length > 0 && (
        <div data-testid="regression-descriptive">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Descriptive Statistics</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#27AE60] text-white">
              {['Variable', 'Mean', 'SD', 'Min', 'Max'].map(h => (
                <th key={h} className="px-3 py-2 text-center font-semibold border border-[#219A52]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(desc).map(([v, s], i) => (
                <tr key={v} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                  <td className="px-3 py-1.5 font-semibold border border-[#E8E6E1]">{v}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Mean?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.SD?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Min?.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono border border-[#E8E6E1]">{s.Max?.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Diagnostic Plots (2x2 grid) */}
      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="regression-diagnostic-plots">
          <div className="bg-[#2980B9]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#2980B9] uppercase tracking-wider">Diagnostic Plots</h4>
            <p className="text-[10px] text-[#8A8580]">Scatter + Regression Line | Residuals vs Fitted | Q-Q Plot | Residuals vs Leverage</p>
          </div>
          <img src={`data:image/png;base64,${data.chart}`} alt="Regression Diagnostic Plots" className="w-full" />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   PATH DIAGRAM (Classic Wright's Style — Reference Layout)
   Blue numbered circles (left) → arrows with Pij labels → Gold circle (right)
   Correlation arcs (rij) between independent vars on far left
============================================================ */


export function MultipleCorrelationView({ data }) {
  if (!data) return null;
  const mc = data.multiple_correlations || {};
  const vars = Object.keys(mc);
  if (!vars.length) return null;

  return (
    <div className="space-y-6" data-testid="multiple-correlation-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Multiple Correlation (R) Table</h4>
        <table className="w-full text-sm border-collapse" data-testid="multiple-corr-table">
          <thead>
            <tr className="bg-[#EAF0EC]">
              {['Dependent Variable', 'R (Multiple)', 'R²', 'Adj. R²', 'F-value', 'P-value', 'Sig.', 'Predictors'].map(h => (
                <th key={h} className="border border-[#D5D1CC] px-3 py-2.5 text-left font-semibold text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vars.map(v => {
              const r = mc[v];
              return (
                <tr key={v} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-[#2C2A28] text-xs">{v}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-xs font-bold text-[#6B8E7B]">{r.R?.toFixed(4)}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-xs text-[#5E5A56]">{r.R_squared?.toFixed(4)}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-xs text-[#5E5A56]">{r.Adjusted_R_squared?.toFixed(4)}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-xs text-[#5E5A56]">{r.F_value?.toFixed(4)}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-xs text-[#5E5A56]">{r.P_value?.toFixed(6)}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.Sig === '**' ? 'bg-emerald-500 text-white' : r.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{r.Sig}</span>
                  </td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-xs text-[#8A8580]">{r.predictors?.join(', ')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   MULTIPLE REGRESSION VIEW
============================================================ */


export function MultipleRegressionView({ data }) {
  if (!data) return null;
  const coeffs = data.coefficients || {};

  return (
    <div className="space-y-6" data-testid="multiple-regression-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Dependent', value: data.dependent },
          { label: 'R²', value: data.r_squared?.toFixed(4) },
          { label: 'Adj. R²', value: data.adjusted_r_squared?.toFixed(4) },
          { label: 'Std. Error', value: data.standard_error?.toFixed(4) },
        ].map(s => (
          <div key={s.label} className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
            <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-1">{s.label}</p>
            <p className="font-mono text-lg font-bold text-[#2C2A28]">{s.value || '-'}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Regression Coefficients</h4>
        <table className="w-full text-sm border-collapse" data-testid="multiple-reg-coefficients">
          <thead>
            <tr className="bg-[#EAF0EC]">
              {['Predictor', 'Coefficient (B)', 'Std. Error', 't-value', 'P-value', 'Sig.'].map(h => (
                <th key={h} className="border border-[#D5D1CC] px-4 py-2.5 text-left font-semibold text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(coeffs).map(([var_name, c]) => (
              <tr key={var_name} className="hover:bg-[#F5F4F2]">
                <td className="border border-[#E8E6E1] px-4 py-2 font-semibold text-[#2C2A28] text-xs">{var_name}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-xs text-[#5E5A56]">{typeof c.coefficient === 'number' ? c.coefficient.toFixed(6) : c.coefficient}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-xs text-[#5E5A56]">{typeof c.std_error === 'number' ? c.std_error.toFixed(6) : c.std_error}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-xs text-[#5E5A56]">{typeof c.t_value === 'number' ? c.t_value.toFixed(4) : c.t_value}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-xs text-[#5E5A56]">{typeof c.p_value === 'number' ? c.p_value.toFixed(6) : c.p_value}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-center">
                  {c.sig && c.sig !== '-' ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${c.sig === '**' ? 'bg-emerald-500 text-white' : c.sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{c.sig}</span>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.predictors && (
        <p className="text-xs text-[#8A8580]">Predictors: {data.predictors.join(', ')} &rarr; {data.dependent}</p>
      )}
      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="Multiple Regression" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" data-testid="multi-reg-chart" />}
    </div>
  );
}

/* ============================================================
   MAIN RESULTS PAGE
============================================================ */
